import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GroupKinds,
  type GroupKind,
  type PlayerGroupNode,
  type PlayerGroupSummary,
} from '@displaycms/contracts';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useAuth } from '@/auth/auth-context';
import {
  createGroup,
  deleteGroup,
  listGroupsTree,
  updateGroup,
  type GroupsTreeResponse,
} from '@/api/groups';
import { ApiError } from '@/api/client';

/**
 * `/groups` — hierarchical group tree with drag-and-drop reparent,
 * inline rename, create / delete.
 *
 * Drag-and-drop is HTML5 native (no extra library). Cycles are rejected
 * client-side AND the server's 400 (which also detects cycles via the
 * pre-update walk) is surfaced as an inline error.
 *
 * Permissions:
 *   - groups.read    → required to view (RequireAuth + RBAC)
 *   - groups.create  → "New group" button
 *   - groups.update  → rename + drag-drop reparent
 *   - groups.delete  → delete (only when empty)
 */

type Lang = 'fr' | 'en';

// Screen-inventory (SP2): a group's semantic level. The server defaults to
// 'zone', so an untouched group reads as "Zone" in the selector and tree.
const DEFAULT_GROUP_KIND: GroupKind = 'zone';

/**
 * Kit Select over the 5 group kinds (site|building|floor|room|zone).
 * Labels come from `groups:kind.*` (duplicated from the players
 * `group_cascader.kind.*` labels so this page stays on its own namespace).
 */
function GroupKindSelect({
  value,
  onChange,
  testId,
  disabled,
}: {
  value: GroupKind;
  onChange: (next: GroupKind) => void;
  testId: string;
  disabled?: boolean;
}): JSX.Element {
  const { t } = useTranslation(['groups']);
  return (
    <Select
      value={value}
      onValueChange={(next) => onChange(next as GroupKind)}
      disabled={disabled ?? false}
    >
      <SelectTrigger data-testid={testId} aria-label={t('groups:kind_label')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {GroupKinds.map((kind) => (
          <SelectItem key={kind} value={kind}>
            {t(`groups:kind.${kind}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface CreateState {
  /** null = root group; otherwise the parent node. */
  parent: PlayerGroupNode | null;
}

interface DeleteState {
  node: PlayerGroupNode;
}

export function GroupsPage(): JSX.Element {
  const { t, i18n: i18next } = useTranslation(['groups', 'common']);
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('groups.create');
  const canUpdate = hasPermission('groups.update');
  const canDelete = hasPermission('groups.delete');
  const lang: Lang = i18next.language === 'en' ? 'en' : 'fr';

  const queryClient = useQueryClient();

  const treeQuery = useQuery<GroupsTreeResponse>({
    queryKey: ['groups', 'tree'],
    queryFn: () => listGroupsTree(),
    staleTime: 30_000,
  });

  const tree = treeQuery.data?.tree ?? [];

  const invalidate = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: ['groups', 'tree'] });
  }, [queryClient]);

  const [createOpen, setCreateOpen] = useState<CreateState | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<DeleteState | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null | 'root'>(null);
  const [error, setError] = useState<string | null>(null);

  const moveMutation = useMutation({
    mutationFn: async (args: { id: string; parent_id: string | null }) =>
      updateGroup(args.id, { parent_id: args.parent_id }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => {
      const key =
        err instanceof ApiError && typeof err.message === 'string' &&
        err.message.includes('cycle')
          ? 'groups:errors.cycle_rejected'
          : 'groups:errors.reparent_failed';
      setError(t(key));
    },
  });

  const renameMutation = useMutation({
    mutationFn: async (args: {
      id: string;
      name_fr: string;
      name_en: string | null;
      kind: GroupKind;
    }) =>
      updateGroup(args.id, {
        name_i18n:
          args.name_en && args.name_en.length > 0
            ? { fr: args.name_fr, en: args.name_en }
            : { fr: args.name_fr },
        kind: args.kind,
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: () => {
      setError(t('groups:errors.rename_failed'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => deleteGroup(id),
    onSuccess: () => {
      setError(null);
      setDeleteOpen(null);
      invalidate();
    },
    onError: () => {
      setError(t('groups:delete_dialog.errors.delete_failed'));
    },
  });

  // Build a descendants map so we can client-side reject obvious cycles.
  const descendants = useMemo(() => collectDescendants(tree), [tree]);

  const handleDrop = useCallback(
    (parentId: string | null) => {
      if (!dragId) return;
      // Reject cycles client-side: cannot drop a node into itself or any
      // of its descendants.
      if (parentId !== null) {
        if (parentId === dragId || descendants.get(dragId)?.has(parentId)) {
          setError(t('groups:errors.cycle_rejected'));
          setDragId(null);
          setDropTargetId(null);
          return;
        }
      }
      moveMutation.mutate({ id: dragId, parent_id: parentId });
      setDragId(null);
      setDropTargetId(null);
    },
    [descendants, dragId, moveMutation, t],
  );

  return (
    <div className="flex flex-col gap-4" data-testid="groups-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('groups:page_title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('groups:page_subtitle')}
          </p>
        </div>
        {canCreate && (
          <Button
            type="button"
            onClick={() => setCreateOpen({ parent: null })}
            data-testid="new-root-group"
          >
            {t('groups:new_root_group')}
          </Button>
        )}
      </header>

      {error && (
        <Alert variant="danger" data-testid="groups-error">
          {error}
        </Alert>
      )}

      {treeQuery.isLoading && (
        <p role="status" data-testid="groups-loading" className="text-sm text-muted-foreground">
          {t('groups:list_loading')}
        </p>
      )}

      {treeQuery.isError && (
        <Alert variant="danger" data-testid="groups-load-error">
          {t('groups:list_error')}
        </Alert>
      )}

      {!treeQuery.isLoading && !treeQuery.isError && tree.length === 0 && (
        <p
          data-testid="groups-empty"
          className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
        >
          {t('groups:empty_state')}
        </p>
      )}

      {tree.length > 0 && (
        <div
          data-testid="groups-tree"
          role="tree"
          aria-label={t('groups:tree_label')}
          className="rounded-md border border-border bg-card p-3"
          onDragOver={(e) => {
            if (canUpdate) {
              e.preventDefault();
              setDropTargetId('root');
            }
          }}
          onDrop={(e) => {
            if (!canUpdate) return;
            e.preventDefault();
            handleDrop(null);
          }}
        >
          {dropTargetId === 'root' && dragId !== null && (
            <div
              data-testid="drop-zone-root"
              className="mb-2 rounded-sm border border-dashed border-primary p-2 text-center text-xs text-[hsl(var(--primary-text))]"
            >
              {t('groups:drop_at_root')}
            </div>
          )}
          <ul className="flex flex-col gap-1">
            {tree.map((node) => (
              <GroupNode
                key={node.id}
                node={node}
                depth={0}
                lang={lang}
                canUpdate={canUpdate}
                canDelete={canDelete}
                canCreate={canCreate}
                onRename={(name_fr, name_en, kind) =>
                  renameMutation.mutate({ id: node.id, name_fr, name_en, kind })
                }
                onChildRename={(id, name_fr, name_en, kind) =>
                  renameMutation.mutate({ id, name_fr, name_en, kind })
                }
                onRequestDelete={(n) => setDeleteOpen({ node: n })}
                onAddChild={(parent) => setCreateOpen({ parent })}
                onDragStartId={setDragId}
                onDropOnNode={(id) => handleDrop(id)}
                onDragOverNode={setDropTargetId}
                dropTargetId={dropTargetId}
                dragId={dragId}
              />
            ))}
          </ul>
        </div>
      )}

      {createOpen && (
        <CreateGroupDialog
          parent={createOpen.parent}
          lang={lang}
          onClose={() => setCreateOpen(null)}
          onCreated={() => {
            setCreateOpen(null);
            invalidate();
          }}
        />
      )}

      {deleteOpen && (
        <DeleteGroupDialog
          node={deleteOpen.node}
          lang={lang}
          isDeleting={deleteMutation.isPending}
          onClose={() => setDeleteOpen(null)}
          onConfirm={() => deleteMutation.mutate(deleteOpen.node.id)}
        />
      )}
    </div>
  );
}

function collectDescendants(
  tree: ReadonlyArray<PlayerGroupNode>,
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  const walk = (node: PlayerGroupNode): Set<string> => {
    const set = new Set<string>();
    for (const child of node.children) {
      set.add(child.id);
      for (const id of walk(child)) set.add(id);
    }
    map.set(node.id, set);
    return set;
  };
  for (const root of tree) walk(root);
  return map;
}

// ---------------------------------------------------------------------------
// GroupNode
// ---------------------------------------------------------------------------

interface GroupNodeProps {
  node: PlayerGroupNode;
  depth: number;
  lang: Lang;
  canUpdate: boolean;
  canDelete: boolean;
  canCreate: boolean;
  onRename: (name_fr: string, name_en: string | null, kind: GroupKind) => void;
  onChildRename: (
    id: string,
    name_fr: string,
    name_en: string | null,
    kind: GroupKind,
  ) => void;
  onRequestDelete: (node: PlayerGroupNode) => void;
  onAddChild: (parent: PlayerGroupNode) => void;
  onDragStartId: (id: string | null) => void;
  onDropOnNode: (parentId: string) => void;
  onDragOverNode: (id: string | null) => void;
  dropTargetId: string | null | 'root';
  dragId: string | null;
}

function GroupNode(props: GroupNodeProps): JSX.Element {
  const {
    node,
    depth,
    lang,
    canUpdate,
    canDelete,
    canCreate,
    onRename,
    onChildRename,
    onRequestDelete,
    onAddChild,
    onDragStartId,
    onDropOnNode,
    onDragOverNode,
    dropTargetId,
    dragId,
  } = props;

  const { t } = useTranslation(['groups']);
  const [editing, setEditing] = useState<boolean>(false);
  const [draftFr, setDraftFr] = useState<string>(node.name_i18n.fr);
  const [draftEn, setDraftEn] = useState<string>(node.name_i18n.en ?? '');
  const nodeKind: GroupKind = node.kind ?? DEFAULT_GROUP_KIND;
  const [draftKind, setDraftKind] = useState<GroupKind>(nodeKind);

  const displayName = lang === 'en' && node.name_i18n.en
    ? node.name_i18n.en
    : node.name_i18n.fr;

  const isDropTarget = dropTargetId === node.id && dragId !== null && dragId !== node.id;
  const isEmpty = node.children.length === 0;

  return (
    <li
      role="treeitem"
      aria-expanded={node.children.length > 0 ? true : undefined}
      data-testid={`group-node-${node.id}`}
      style={{ paddingLeft: `${depth * 16}px` }}
    >
      <div
        className={`flex items-center justify-between gap-2 rounded-md border ${
          isDropTarget ? 'border-primary bg-primary/5' : 'border-transparent'
        } p-2`}
        draggable={canUpdate}
        onDragStart={(e) => {
          if (!canUpdate) return;
          onDragStartId(node.id);
          // jsdom (used in tests) does not populate `dataTransfer` on
          // synthetic events — guard so the page does not throw.
          try {
            if (e.dataTransfer) {
              e.dataTransfer.effectAllowed = 'move';
              // Required for Firefox to register the drag in real browsers.
              e.dataTransfer.setData('text/plain', node.id);
            }
          } catch {
            // ignore
          }
        }}
        onDragEnd={() => onDragStartId(null)}
        onDragOver={(e) => {
          if (!canUpdate) return;
          e.preventDefault();
          e.stopPropagation();
          onDragOverNode(node.id);
        }}
        onDrop={(e) => {
          if (!canUpdate) return;
          e.preventDefault();
          e.stopPropagation();
          onDropOnNode(node.id);
        }}
        data-testid={`group-row-${node.id}`}
      >
        {editing ? (
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <label className="flex flex-col text-xs">
              <span className="text-muted-foreground">
                {t('groups:node.name_fr_label')}
              </span>
              <Input
                value={draftFr}
                onChange={(e) => setDraftFr(e.target.value)}
                data-testid={`rename-fr-${node.id}`}
                className="h-8 w-44"
              />
            </label>
            <label className="flex flex-col text-xs">
              <span className="text-muted-foreground">
                {t('groups:node.name_en_label')}
              </span>
              <Input
                value={draftEn}
                onChange={(e) => setDraftEn(e.target.value)}
                data-testid={`rename-en-${node.id}`}
                className="h-8 w-44"
              />
            </label>
            <div className="flex flex-col text-xs">
              <span className="text-muted-foreground">
                {t('groups:kind_label')}
              </span>
              <GroupKindSelect
                value={draftKind}
                onChange={setDraftKind}
                testId={`rename-kind-${node.id}`}
              />
            </div>
            <Button
              type="button"
              size="sm"
              data-testid={`rename-save-${node.id}`}
              onClick={() => {
                if (draftFr.trim().length === 0) return;
                onRename(draftFr.trim(), draftEn.trim() || null, draftKind);
                setEditing(false);
              }}
            >
              {t('groups:node.save')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid={`rename-cancel-${node.id}`}
              onClick={() => {
                setDraftFr(node.name_i18n.fr);
                setDraftEn(node.name_i18n.en ?? '');
                setDraftKind(nodeKind);
                setEditing(false);
              }}
            >
              {t('groups:node.cancel')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-1 items-center gap-2">
            {canUpdate && (
              <span
                aria-hidden="true"
                className="cursor-grab text-muted-foreground"
                data-testid={`drag-handle-${node.id}`}
                title={t('groups:node.drag_handle')}
              >
                ⋮⋮
              </span>
            )}
            <span
              className="font-medium"
              data-testid={`group-name-${node.id}`}
            >
              {displayName}
            </span>
            <span
              className="rounded-sm border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
              data-testid={`group-kind-${node.id}`}
            >
              {t(`groups:kind.${nodeKind}`)}
            </span>
          </div>
        )}

        {!editing && (
          <div className="flex items-center gap-1">
            {canCreate && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onAddChild(node)}
                data-testid={`add-child-${node.id}`}
              >
                {t('groups:node.add_child')}
              </Button>
            )}
            {canUpdate && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setDraftFr(node.name_i18n.fr);
                  setDraftEn(node.name_i18n.en ?? '');
                  setDraftKind(nodeKind);
                  setEditing(true);
                }}
                data-testid={`rename-${node.id}`}
              >
                {t('groups:node.rename')}
              </Button>
            )}
            {canDelete && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onRequestDelete(node)}
                disabled={!isEmpty}
                data-testid={`delete-${node.id}`}
                title={
                  isEmpty
                    ? t('groups:node.delete')
                    : t('groups:delete_dialog.body_not_empty')
                }
              >
                {t('groups:node.delete')}
              </Button>
            )}
          </div>
        )}
      </div>

      {node.children.length > 0 && (
        <ul role="group" className="flex flex-col gap-1">
          {node.children.map((child) => (
            <GroupNode
              key={child.id}
              node={child}
              depth={depth + 1}
              lang={lang}
              canUpdate={canUpdate}
              canDelete={canDelete}
              canCreate={canCreate}
              onRename={(fr, en, kind) => onChildRename(child.id, fr, en, kind)}
              onChildRename={onChildRename}
              onRequestDelete={onRequestDelete}
              onAddChild={onAddChild}
              onDragStartId={onDragStartId}
              onDropOnNode={onDropOnNode}
              onDragOverNode={onDragOverNode}
              dropTargetId={dropTargetId}
              dragId={dragId}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// CreateGroupDialog
// ---------------------------------------------------------------------------

interface CreateGroupDialogProps {
  parent: PlayerGroupNode | null;
  lang: Lang;
  onClose: () => void;
  onCreated: (created: PlayerGroupSummary) => void;
}

function CreateGroupDialog({
  parent,
  lang,
  onClose,
  onCreated,
}: CreateGroupDialogProps): JSX.Element {
  const { t } = useTranslation(['groups']);
  const [nameFr, setNameFr] = useState<string>('');
  const [nameEn, setNameEn] = useState<string>('');
  const [kind, setKind] = useState<GroupKind>(DEFAULT_GROUP_KIND);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  const parentLabel = parent
    ? lang === 'en' && parent.name_i18n.en
      ? parent.name_i18n.en
      : parent.name_i18n.fr
    : '';

  const submit = useCallback(async (): Promise<void> => {
    if (nameFr.trim().length === 0) {
      setErr(t('groups:create_dialog.errors.missing_name'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const created = await createGroup({
        parent_id: parent?.id ?? null,
        name_i18n:
          nameEn.trim().length > 0
            ? { fr: nameFr.trim(), en: nameEn.trim() }
            : { fr: nameFr.trim() },
        kind,
      });
      onCreated(created);
    } catch {
      setErr(t('groups:create_dialog.errors.create_failed'));
    } finally {
      setSubmitting(false);
    }
  }, [nameFr, nameEn, kind, onCreated, parent?.id, t]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="create-group-dialog"
        className="max-w-md"
        aria-label={
          parent
            ? t('groups:create_dialog.title_child', { parent: parentLabel })
            : t('groups:create_dialog.title_root')
        }
      >
        <DialogHeader>
          <DialogTitle data-testid="create-group-title">
            {parent
              ? t('groups:create_dialog.title_child', { parent: parentLabel })
              : t('groups:create_dialog.title_root')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">
              {t('groups:create_dialog.name_fr_label')}
            </span>
            <Input
              value={nameFr}
              onChange={(e) => setNameFr(e.target.value)}
              data-testid="create-group-fr"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">
              {t('groups:create_dialog.name_en_label')}
            </span>
            <Input
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              data-testid="create-group-en"
            />
          </label>
          <div className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('groups:kind_label')}</span>
            <GroupKindSelect
              value={kind}
              onChange={setKind}
              testId="create-group-kind"
            />
          </div>
        </div>
        {err && (
          <Alert variant="danger" className="mt-2" data-testid="create-group-error">
            {err}
          </Alert>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="create-group-cancel"
          >
            {t('groups:create_dialog.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void submit();
            }}
            disabled={submitting}
            data-testid="create-group-submit"
          >
            {submitting
              ? t('groups:create_dialog.creating')
              : t('groups:create_dialog.create')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// DeleteGroupDialog
// ---------------------------------------------------------------------------

interface DeleteGroupDialogProps {
  node: PlayerGroupNode;
  lang: Lang;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function DeleteGroupDialog({
  node,
  lang,
  isDeleting,
  onClose,
  onConfirm,
}: DeleteGroupDialogProps): JSX.Element {
  const { t } = useTranslation(['groups']);
  const name =
    lang === 'en' && node.name_i18n.en ? node.name_i18n.en : node.name_i18n.fr;
  const isEmpty = node.children.length === 0;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="delete-group-dialog"
        className="max-w-md"
        aria-label={t('groups:delete_dialog.title')}
      >
        <DialogHeader>
          <DialogTitle>{t('groups:delete_dialog.title')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm" data-testid="delete-group-body">
          {isEmpty
            ? t('groups:delete_dialog.body', { name })
            : t('groups:delete_dialog.body_not_empty')}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="delete-group-cancel"
          >
            {t('groups:delete_dialog.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!isEmpty || isDeleting}
            data-testid="delete-group-confirm"
          >
            {isDeleting
              ? t('groups:delete_dialog.deleting')
              : t('groups:delete_dialog.confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GroupsPage;
